// Inbox data for the tenant workspace (mockups 21/22). The agent-facing surface
// for conversations the runtime drives — incl. the handoffs it generates.
//
// Wired: listConversations() -> GET /conversations, returning
// fully-hydrated conversations (list card + contact panel + thread). The write
// actions (G-L2) call the B-L2 mutation endpoints: reply / note, return-to-bot,
// and resolve.

import { apiClient } from '../../api/client.js'
import type { ContactPanelSection, ContactTag } from '../../design-system/index.js'

export type ThreadItem =
  | {
      type: 'msg'
      id: string
      sender: 'user' | 'bot' | 'agent'
      author?: string
      time: string
      text: string
    }
  | { type: 'note'; id: string; author: string; time: string; text: string }
  | {
      type: 'event'
      id: string
      kind: 'sensitive' | 'handoff' | 'resolved' | 'reopened'
      text: string
    }
  | { type: 'day'; id: string; label: string }

/** Quién tiene la conversación, visto por quien la lee. 'other' es una
 *  COMPAÑERA que ya la está atendiendo: antes llegaba como 'unassigned', así
 *  que la bandeja ofrecía "Tomar conversación" sobre el hilo de alguien más. */
export type Assignment = 'you' | 'bot' | 'unassigned' | 'other'

export interface Conversation {
  id: string
  contact: { id?: string; name: string; initials: string; phone?: string }
  /** The channel this conversation actually arrived on, and its display name. */
  channel?: string
  channelLabel?: string
  snippet: string
  time: string
  unread: boolean
  priority?: 'prio' | 'sens'
  assignment: Assignment
  /** Nombre de quien la tiene, o null si no la tiene nadie. Con 'other' es lo
   *  que convierte "es de alguien más" en algo con lo que se puede actuar. */
  holder?: string | null
  /** WhatsApp 24h service window; <= 0 means closed (free replies blocked). */
  windowHoursLeft: number
  tags: ContactTag[]
  sections: ContactPanelSection[]
  notes: string
  thread: ThreadItem[]
}

/**
 * GET /conversations — fully-hydrated conversations for the inbox: list card,
 * contact panel, thread and the WhatsApp 24h-window state in one call.
 */
/**
 * The inbox list. `assignedToMe` is the "Contigo" tab, and it is a SERVER
 * question: the list is capped at 50 rows, so filtering the returned page for
 * your own conversations quietly hid the older ones — the tab claimed to show
 * your work and showed your work from this page only.
 */
export async function listConversations(assignedToMe = false): Promise<Conversation[]> {
  const res = await apiClient.get<{ data: Conversation[] }>(
    assignedToMe ? '/conversations?assigned=me' : '/conversations',
  )
  return res.data.data
}

// ─── Write actions (B-L2 endpoints) ──────────────────────────────────────────

function path(id: string, action: string): string {
  return `/conversations/${encodeURIComponent(id)}/${action}`
}

/**
 * Send an agent reply (mode 'reply' — claims the conversation + enqueues the
 * WhatsApp send) or add a team-only internal note (mode 'note').
 */
export async function replyConversation(
  id: string,
  text: string,
  mode: 'reply' | 'note',
): Promise<void> {
  await apiClient.post(path(id, 'reply'), { text, mode })
}

/** Hand the conversation back to the bot. */
export async function returnConversationToBot(id: string): Promise<void> {
  await apiClient.post(path(id, 'return-to-bot'), {})
}

/** Mark the conversation resolved. */
export async function resolveConversation(id: string): Promise<void> {
  await apiClient.post(path(id, 'resolve'), {})
}

/** The Bandeja badge's number: customers waiting for a human right now.
 *  Cheap count endpoint — the shell polls it ambiently. */
export async function getWaitingCount(): Promise<number> {
  const res = await apiClient.get<{ data: { waiting: number } }>('/conversations/waiting-count')
  return res.data.data.waiting
}
